"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { Task, TaskStatus, User } from "@/schema"
import { useGlobalSlideoverStore } from "@/stores/use-global-slideover-store"
import { useQueryClient } from "@tanstack/react-query"
import DataError from "@ui/data-error"
import {
  badgeVariants,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@ui/shad"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@ui/tab-pages"
import { cn } from "@ui/utils"
import {
  Brain,
  ClipboardList,
  History,
  Loader2,
  MessageSquare,
  MoreHorizontal,
  SlidersHorizontal,
  ThumbsDown,
  ThumbsUp,
  Trash2,
} from "lucide-react"
import { toast } from "sonner"

import { useProject } from "@/hooks/api/projects/queries"
import { useDeleteTask, useFetchTask, useTaskCompletion, useUpdateTask } from "@/hooks/api/tasks"
import { AuditLogEntry } from "@/components/audit-logs/audit-log-access"
import { Link } from "@/components/book-link"
import { CommentSection } from "@/components/comments"
import { ConfirmDialog } from "@/components/dialogs"
import { ComposeEmailPreview } from "@/components/email/compose-preview"
import { EditorRefHandle } from "@/components/lexical"
import { hasVisitedMilestoneFormFields, ProjectFormResponses } from "@/components/projects/project-form-responses"
import { CollaboratorSelector } from "@/components/users/collaborator-selector"

import { TaskVersionHistoryDialog } from "../task-version-history-dialog"
import TaskDescription from "./task-description"
import TaskHeader from "./task-header"
import TaskMetadata from "./task-metadata"
import TaskSkeleton from "./task-skeleton"

type TaskDetailTab = "properties" | "comments"

interface TaskDetailSlideoverProps {
  taskId: string | null
  onComplete?: (taskId: string | undefined, completed: boolean) => void
  initialTab?: TaskDetailTab
}

export function TaskDetailSlideover({ taskId, onComplete, initialTab = "properties" }: TaskDetailSlideoverProps) {
  const {
    data: task,
    isLoading,
    refetch,
    isRefetching,
  } = useFetchTask(taskId || "", {
    enabled: !!taskId,
  })

  const isCompleted = task?.status === TaskStatus.DONE || task?.optimistic_status === TaskStatus.DONE
  const completedAt = task?.completed_at

  const [activeTab, setActiveTab] = useState<string>(initialTab)
  const [commentCount, setCommentCount] = useState(task?.comment_count ?? 0)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showVersionHistory, setShowVersionHistory] = useState(false)
  const [contentEditorRevision, setContentEditorRevision] = useState(0)

  const queryClient = useQueryClient()
  const updateTaskMutation = useUpdateTask()
  const deleteTaskMutation = useDeleteTask()
  const { handleTaskComplete: defaultHandleTaskComplete } = useTaskCompletion()
  const descriptionEditorRef = useRef<EditorRefHandle>(null)
  const { data: project } = useProject(task?.project_id ?? "", { enabled: !!task?.project_id })

  const focusDescriptionEditor = () => {
    descriptionEditorRef.current?.focus()
  }

  const handleTaskComplete = (checked: boolean) => {
    if (!task) return

    if (onComplete) {
      onComplete(task.id, !!checked)
    } else {
      defaultHandleTaskComplete(task.id, checked)
      // Optimistically update the task query keyed by the prop taskId, which may
      // differ from task.id (e.g., when opened from a notification)
      queryClient.setQueryData(["task", taskId], (old: Task | undefined) =>
        old
          ? {
              ...old,
              status: checked ? TaskStatus.DONE : TaskStatus.TODO,
              optimistic_status: checked ? TaskStatus.DONE : TaskStatus.TODO,
              completed_at: checked ? new Date().toISOString() : null,
            }
          : old
      )
    }
  }

  const handleUpdateTask = useCallback(
    (payload: Partial<Task> & { id: string }, options?: { onSuccess?: () => void }) => {
      const promise = updateTaskMutation.mutateAsync(payload).then(() => {
        options?.onSuccess?.()
      })
      promise.catch(() => undefined)
      return promise
    },
    [updateTaskMutation]
  )

  const handleAddCollaborator = useCallback(
    (user: User) => {
      if (!task || !task.id) return

      const existingCollaboratorIds = task.collaborator_ids || []
      const collaboratorIds = Array.from(new Set([...existingCollaboratorIds, user.id]))

      updateTaskMutation.mutate(
        {
          id: task.id,
          collaborator_ids: collaboratorIds,
        } as Partial<Task> & { id: string },
        {
          onSuccess: () => {
            toast.success("Collaborator added successfully")
          },
        }
      )
    },
    [task, updateTaskMutation]
  )

  const handleRemoveCollaborator = useCallback(
    (userId: string) => {
      if (!task || !task.id) return

      const collaboratorIds = task.collaborator_ids?.filter((c) => c !== userId) || []

      updateTaskMutation.mutate(
        {
          id: task.id,
          collaborator_ids: collaboratorIds,
        } as Partial<Task> & { id: string },
        {
          onSuccess: () => {
            toast.success("Collaborator removed successfully")
          },
        }
      )
    },
    [task, updateTaskMutation]
  )

  const handleAcceptTask = () => {
    if (!task?.id) return
    updateTaskMutation.mutate(
      { id: task.id, status: TaskStatus.TODO },
      {
        onSuccess: (updatedTask) => {
          // The mutation only updates ["task", task.id]; also update the cache for the
          // lookup id this slideover was opened with, which may differ (see handleTaskComplete)
          queryClient.setQueryData(["task", taskId], updatedTask)
          toast.success("Task accepted")
        },
      }
    )
  }

  const handleRejectTask = () => {
    if (!task?.id) return
    updateTaskMutation.mutate(
      { id: task.id, status: TaskStatus.REJECTED },
      {
        onSuccess: (updatedTask) => {
          queryClient.setQueryData(["task", taskId], updatedTask)
          toast.success("Task rejected")
        },
      }
    )
  }

  const handleDeleteTask = useCallback(() => {
    if (!task?.id) return

    deleteTaskMutation.mutate(task.id, {
      onSuccess: () => {
        toast.success("Task deleted successfully")
        setShowDeleteConfirm(false)
        const store = useGlobalSlideoverStore.getState()
        if (store.canGoBack) {
          store.popSlideover()
        } else {
          store.closeSlideover()
        }
      },
    })
  }, [task?.id, deleteTaskMutation])

  useEffect(() => {
    setCommentCount(task?.comment_count ?? 0)
  }, [task?.id, task?.comment_count])

  useEffect(() => {
    if (task) {
      useGlobalSlideoverStore.getState().setRightHeaderContent(
        <div className="flex items-center gap-1">
          <CollaboratorSelector
            collaboratorIds={task.collaborator_ids || []}
            onAdd={handleAddCollaborator}
            onRemove={handleRemoveCollaborator}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="text-icon" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <AuditLogEntry>
                <DropdownMenuItem asChild>
                  <Link
                    href={`/audit-log/task/${task.id}`}
                    bookIdOverride={task.book_id}
                    onClick={() => useGlobalSlideoverStore.getState().closeSlideover()}
                  >
                    <History />
                    View audit log
                  </Link>
                </DropdownMenuItem>
              </AuditLogEntry>
              <DropdownMenuItem onSelect={() => setShowVersionHistory(true)}>
                <History />
                Version history
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setShowDeleteConfirm(true)}>
                <Trash2 className="text-destructive" />
                Delete task
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )
    }
  }, [task, handleAddCollaborator, handleRemoveCollaborator])

  useEffect(() => {
    setActiveTab(initialTab)
    setShowVersionHistory(false)
  }, [task?.id, initialTab])

  if (isLoading) {
    return (
      <div className="">
        <TaskSkeleton />
      </div>
    )
  }

  if (!task) {
    return (
      <DataError
        title="No task data available"
        description="We are having issues loading this task. Please try again later."
        action={
          <Button onClick={() => refetch()} disabled={isRefetching}>
            {isRefetching && <Loader2 className="animate-spin" />}
            {isRefetching ? "Loading..." : "Retry"}
          </Button>
        }
      />
    )
  }

  const hasFormResponses = hasVisitedMilestoneFormFields(project)

  const recommendedActionDraftId =
    task.actionable?.type === "email" && typeof task.actionable.syncbox_draft_id === "string"
      ? task.actionable.syncbox_draft_id
      : null
  const hasRecommendedAction = Boolean(recommendedActionDraftId)
  const showCommentBadge = commentCount > 0
  const handleVersionRestored = (restoredTask: Task) => {
    queryClient.setQueryData(["task", taskId], restoredTask)
    queryClient.setQueryData(["task", restoredTask.id], restoredTask)
    setContentEditorRevision((revision) => revision + 1)
  }

  return (
    <div className="pb-16">
      {task.status === TaskStatus.PENDING && (
        <div className="mb-3 flex items-center gap-2">
          <Button variant="outlineSuccess" size="xs" onClick={handleAcceptTask} disabled={updateTaskMutation.isPending}>
            <ThumbsUp />
            Accept
          </Button>
          <Button
            variant="outlineDestructive"
            size="xs"
            onClick={handleRejectTask}
            disabled={updateTaskMutation.isPending}
          >
            <ThumbsDown />
            Reject
          </Button>
        </div>
      )}
      <TaskHeader
        key={`title-${task.id}-${contentEditorRevision}`}
        task={task}
        isCompleted={isCompleted}
        onComplete={handleTaskComplete}
        onEnterPressNoMention={focusDescriptionEditor}
        onUpdateTask={handleUpdateTask}
      />
      <TaskDescription
        key={`description-${task.id}-${contentEditorRevision}`}
        task={task}
        isCompleted={isCompleted}
        descriptionEditorRef={descriptionEditorRef}
        onUpdateTask={handleUpdateTask}
      />

      <div className="mt-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <TabsList className="px-0">
            <TabsTrigger value="properties" icon={<SlidersHorizontal />}>
              Overview
            </TabsTrigger>
            {hasFormResponses && (
              <TabsTrigger value="form-responses" icon={<ClipboardList />}>
                Form responses
              </TabsTrigger>
            )}
            {hasRecommendedAction && (
              <TabsTrigger value="ai-action" icon={<Brain />}>
                AI action
              </TabsTrigger>
            )}
            <TabsTrigger value="comments" icon={<MessageSquare />}>
              <span className="inline-flex items-center gap-1.5">
                <span>Comments</span>
                {showCommentBadge && (
                  <span className={cn(badgeVariants({ variant: "secondary", size: "xs" }))}>{commentCount}</span>
                )}
              </span>
            </TabsTrigger>
          </TabsList>

          <TabsContent key="properties" value="properties" className="pt-4" disableScrollArea forceMount>
            <TaskMetadata
              task={task}
              isCompleted={isCompleted}
              completedAt={completedAt}
              onUpdateTask={handleUpdateTask}
            />
          </TabsContent>

          {hasFormResponses && project && (
            <TabsContent key="form-responses" value="form-responses" disableScrollArea forceMount>
              <ProjectFormResponses project={project} />
            </TabsContent>
          )}

          {hasRecommendedAction && (
            <TabsContent key="ai-action" value="ai-action" className="pt-4" disableScrollArea forceMount>
              <ComposeEmailPreview
                syncboxDraftId={recommendedActionDraftId ?? undefined}
                onTaskComplete={() => handleTaskComplete(true)}
              />
            </TabsContent>
          )}

          <TabsContent key="comments" value="comments" className="flex flex-col pt-4" disableScrollArea forceMount>
            <div className="flex-1 overflow-y-auto">
              <CommentSection resourceType="Task" resourceId={task.id} onCommentCountChange={setCommentCount} />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <TaskVersionHistoryDialog
        task={task}
        open={showVersionHistory}
        onOpenChange={setShowVersionHistory}
        onRestored={handleVersionRestored}
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title={"Delete task?"}
        description={"This action cannot be undone. This will permanently delete the task."}
        dismiss={"explicit"}
        cancel={{ onClick: () => setShowDeleteConfirm(false) }}
        action={{ label: "Delete", onClick: handleDeleteTask }}
      />
    </div>
  )
}

export default TaskDetailSlideover
